import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { TournamentPass } from '@fantaz/shared/data-access/models';
import * as PassesActions from './passes.actions';

export const PASSES_FEATURE_KEY = 'tournament-passes';

export interface State extends EntityState<TournamentPass> {
  selectedId?: string | number; // which Passes record has been selected
  loaded: boolean; // has the Passes list been loaded
  error?: string | null; // last known error (if any)
  query?: {
    eventPrototypeUuid: string;
    eventScheduleUuid: string;
    userId?: string;
  };
  buyingTournamentPass?: boolean;
}

export interface PassesPartialState {
  readonly [PASSES_FEATURE_KEY]: State;
}

export const passesAdapter: EntityAdapter<TournamentPass> =
  createEntityAdapter<TournamentPass>({
    selectId: (pass: TournamentPass) =>
      pass.fantazPassUuid ? pass.fantazPassUuid : pass.uuid,
  });

export const passesInitialState: State = passesAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  buyingTournamentPass: false,
});

const passesReducer = createReducer(
  passesInitialState,
  on(PassesActions.init, (state) => ({ ...state, loaded: false, error: null })),
  on(PassesActions.loadTournamentPasses, (state, args) => ({
    ...state,
    query: args,
    loaded: false,
    error: null,
  })),
  on(PassesActions.loadTournamentPasses, (state, args) => ({
    ...state,
    query: args,
    loaded: false,
    error: null,
  })),
  on(PassesActions.loadPassesSuccess, (state, { passes }) =>
    passesAdapter.setAll(passes, { ...state, loaded: true }),
  ),
  on(PassesActions.loadPassesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(PassesActions.buyTournamentPass, (state, { fantazPassUuid }) =>
    passesAdapter.updateMany(
      [
        ...(state.ids.map((id) => ({
          id,
          changes: {
            disabled: true,
          },
        })) as []),
        {
          id: fantazPassUuid,
          changes: {
            processing: true,
          },
        },
      ],
      { ...state, buyingTournamentPass: true },
    ),
  ),
  on(PassesActions.buyPassSuccess, (state, { pass, fantazPassUuid }) =>
    passesAdapter.updateMany(
      [
        ...(state.ids.map((id) => ({
          id,
          changes: {
            disabled: true,
          },
        })) as []),
        {
          id: fantazPassUuid,
          changes: {
            ...pass,
            processing: false,
            disabled: false,
          },
        },
      ],
      { ...state, buyingTournamentPass: false },
    ),
  ),
  on(PassesActions.buyPassFailure, (state, { fantazPassUuid }) =>
    passesAdapter.updateMany(
      [
        ...(state.ids.map((id) => ({
          id,
          changes: {
            disabled: false,
            processing: false,
          },
        })) as []),
        {
          id: fantazPassUuid,
          changes: {
            processing: false,
            disabled: false,
          },
        },
      ],
      { ...state, buyingTournamentPass: false },
    ),
  ),
  on(PassesActions.clearPasses, (state) => {
    return passesAdapter.setAll([], { ...state, loaded: true, ids: [] });
  }),
  on(PassesActions.updatePassLocally, (state, { uuid, updates }) =>
    passesAdapter.updateOne(
      {
        id: uuid,
        changes: {
          ...updates,
        },
      },
      { ...state },
    ),
  ),
);

export function reducer(state: State | undefined, action: Action) {
  return passesReducer(state, action);
}
